import { JSX, PropsWithChildren } from 'react';
import { generatePath, Navigate, useNavigate, useParams } from 'react-router-dom';

import { EPermissions } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Modal from '@ui/modal/modal';
import FolderPaths from '@/utils/images';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import './route-guard.css';
import GradientLoader from '@ui/loader/gradient-loader';

export interface RouteGuardProps {
  allowedRoles?: EPermissions[];
  blockedRoles?: EPermissions[];
  redirectTo?: string;
  unauthorizedMessage?: string;
  showUnauthorizedMessage?: boolean;
}

export default function RouteGuard({
  allowedRoles,
  blockedRoles,
  redirectTo,
  unauthorizedMessage,
  children,
}: PropsWithChildren<RouteGuardProps>): JSX.Element {
  const { hasPermission, isLoading } = useHasPermission({ allowedRoles, blockedRoles });
  const params = useParams();
  const navigate = useNavigate();

  const handleRedirect = () => {
    if (!redirectTo) return;
    // If redirectTo is a full URL, redirect to it
    if (redirectTo.startsWith('http')) {
      window.location.href = redirectTo;
      return;
    }
    const route = generatePath(redirectTo, params);
    navigate(route, { replace: true });
  };

  if (isLoading) {
    return (
      <Container data-component="RouteGuard" className="flex jc-center ai-center height-100 padding-32">
        <GradientLoader />
      </Container>
    );
  }

  if (hasPermission) {
    return <>{children}</>;
  }

  if (unauthorizedMessage) {
    return (
      <Modal
        data-component="RouteGuard"
        contentWrapperClassName="padding-v-40 padding-h-24 route-guard-modal"
        open={true}
        customSize={{
          width: '640px',
          maxWidth: '640px',
          height: 'auto',
          maxHeight: 'auto',
          minHeight: 'auto',
        }}
        RenderHeaderContent={() => <Text className="H4">Access Denied</Text>}
        RenderFooterContent={() => (
          <div className="flex jc-flex-end">
            <Button
              className="flex ai-center jc-center gap-8 "
              onClick={handleRedirect}
              backgroundColor="var(--gray-600)"
            >
              <Icon name={ICONS.ARROW_LEFT} size={16} />
              <Text className="L1">Back</Text>
            </Button>
          </div>
        )}
      >
        <div className="flex fd-column jc-center ai-center gap-24 padding-v-24 padding-h-40">
          <img
            className="route-guard-image"
            style={{ height: 140, width: 140 }}
            src={FolderPaths.TellyTired}
            alt="Access Denied"
          />
          <Text className="S1 text-center">{unauthorizedMessage}</Text>
        </div>
      </Modal>
    );
  }

  if (redirectTo) {
    // If redirectTo is a full URL, redirect to it
    if (redirectTo.startsWith('http')) {
      window.location.href = redirectTo;
    }
    const route = generatePath(redirectTo, params);

    return <Navigate to={route} replace />;
  }

  return <></>;
}
